import { apiRequest } from './client';
import type { components } from './generated/schema';
import type { Delivery, JawType } from '../types/admin';

export type AlignerSetStatus = components['schemas']['AlignerSetStatus'];

// --- Delivery ---

export type CreateDeliveryPayload = {
  readonly delivery_address_id: string;
};

export type UpdateDeliveryPayload = {
  readonly tracking_number?: string | null;
  readonly delivery_status?: string | null;
  readonly notes?: string | null;
};

export type AdminDeliveryResponse = Delivery & {
  readonly updated_by_admin_id: string | null;
  readonly received_confirmed_by_patient_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
};

export function createCycleDelivery(
  patientId: string,
  treatmentId: string,
  cycleId: string,
  payload: CreateDeliveryPayload,
  accessToken: string,
): Promise<AdminDeliveryResponse> {
  return apiRequest<AdminDeliveryResponse>(
    `/admin/patients/${patientId}/treatments/${treatmentId}/cycles/${cycleId}/delivery`,
    { method: 'POST', body: payload, token: accessToken },
  );
}

export function updateCycleDelivery(
  patientId: string,
  treatmentId: string,
  cycleId: string,
  deliveryId: string,
  payload: UpdateDeliveryPayload,
  accessToken: string,
): Promise<AdminDeliveryResponse> {
  return apiRequest<AdminDeliveryResponse>(
    `/admin/patients/${patientId}/treatments/${treatmentId}/cycles/${cycleId}/delivery/${deliveryId}`,
    { method: 'PATCH', body: payload, token: accessToken },
  );
}

export function confirmDeliveryReceived(
  patientId: string,
  treatmentId: string,
  cycleId: string,
  accessToken: string,
): Promise<void> {
  return apiRequest<void>(
    `/admin/patients/${patientId}/treatments/${treatmentId}/cycles/${cycleId}/confirm-received`,
    { method: 'POST', token: accessToken },
  );
}

// --- Aligner Sets ---

export type CreateAlignerSetsPayload = {
  readonly jaw_type: JawType;
  readonly count: number;
  readonly target_wear_days?: number | null;
  readonly wear_schedule?: 'full_day' | 'night_only';
  readonly replace?: boolean;
};

export type AlignerSetItem = {
  readonly id: string;
  readonly jaw_type: JawType;
  readonly aligner_number: number;
  readonly status: string;
  readonly wear_schedule: string;
  readonly target_wear_days: number | null;
};

export function createCycleAlignerSets(
  patientId: string,
  treatmentId: string,
  cycleId: string,
  payload: CreateAlignerSetsPayload,
  accessToken: string,
): Promise<readonly AlignerSetItem[]> {
  return apiRequest<readonly AlignerSetItem[]>(
    `/admin/patients/${patientId}/treatments/${treatmentId}/cycles/${cycleId}/aligner-sets`,
    { method: 'POST', body: payload, token: accessToken },
  );
}

/**
 * PATCH payload for an aligner set. Field presence is meaningful: omit a key to
 * leave the value untouched, send `null` to clear a stamp (backend uses
 * `model_fields_set` semantics). Build with `buildAlignerSetPatch` rather than
 * by hand to avoid sending unchanged fields.
 */
export type UpdateAlignerSetPayload = {
  readonly total_worn_minutes?: number | null;
  readonly started_at?: string | null;
  readonly paused_at?: string | null;
  readonly completed_at?: string | null;
  readonly status?: AlignerSetStatus | null;
};

export type AdminAlignerSetResponse = components['schemas']['AdminAlignerSetItem'];

export function updateCycleAlignerSet(
  patientId: string,
  treatmentId: string,
  cycleId: string,
  alignerSetId: string,
  payload: UpdateAlignerSetPayload,
  accessToken: string,
): Promise<AdminAlignerSetResponse> {
  return apiRequest<AdminAlignerSetResponse>(
    `/admin/patients/${patientId}/treatments/${treatmentId}/cycles/${cycleId}/aligner_sets/${alignerSetId}`,
    { method: 'PATCH', body: payload, token: accessToken },
  );
}
